import {useCallback, useEffect, useMemo, useState} from 'react';
import {QueryStatus} from '@reduxjs/toolkit/query/react';
import type {OverviewTaskType} from 'services/settings/types';
import type {OverviewTask, TaskTypeAll} from 'services/tasks/types';
import {Configuration} from 'configuration';
import {useLocalization} from 'localization';
import {useGetLocksQuery} from 'services/locks/locksApi';
import {
  overviewSelectedTaskTypeUpdated,
  selectSettingsOverviewSearchText,
  selectSettingsOverviewSelectedTaskType,
} from 'services/settings';
import {getTaskTypes} from 'services/settings/functions/getTaskTypes';
import {mapTasksToOverviewTasks} from 'services/tasks';
import {useGetTasksQuery} from 'services/tasks/tasksApi';
import {useGetUsersQuery} from 'services/users/usersApi';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import {useSearchedTasks} from './useSearchedTasks';
import {useSetTaskSearchText} from './useSetTaskSearchText';

const LOCKS_RELOAD_FALLBACK_MS = 1000 * 5;
const LOCKS_RELOAD_MS = Configuration.interval.lock ?? LOCKS_RELOAD_FALLBACK_MS;
const LOCKS_DELAY_MS = 400;

export const useOverviewTasks = (): {
  allTasks: OverviewTask[];
  filteredTasks: OverviewTask[];
  taskTypes: OverviewTaskType[];
  selectedTaskType: TaskTypeAll;
  setSelectedTaskType: (taskType: TaskTypeAll) => void;
  searchText: string;
  setSearchText: (searchText: string) => void;
  loading: boolean;
  errorMsg?: string;
} => {
  const localization = useLocalization();

  const dispatch = useAppDispatch();
  const selectedTaskType = useAppSelector(
    selectSettingsOverviewSelectedTaskType,
  );
  const searchText = useAppSelector(selectSettingsOverviewSearchText);
  const setSearchText = useSetTaskSearchText();

  const {data: tasksFromApi, status, error} = useGetTasksQuery();

  // Delay polling to allow recently locked locks to be released before first load.
  const [startPolling, setStartPolling] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setStartPolling(true);
    }, LOCKS_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const {data: allLocks} = useGetLocksQuery(undefined, {
    skip: !startPolling,
    pollingInterval: LOCKS_RELOAD_MS,
    // limits re-renders to actual lock changes (not status, etc)
    selectFromResult: ({data}) => ({data}),
  });

  const {data: users} = useGetUsersQuery();

  const allTasks = useMemo(
    (): OverviewTask[] =>
      mapTasksToOverviewTasks(tasksFromApi || [], localization),
    [localization, tasksFromApi],
  );

  const tasksWithLocks = useMemo((): OverviewTask[] => {
    if (!allLocks || allLocks.length === 0 || allTasks.length === 0 || !users) {
      return allTasks;
    }
    const workLocks = new Map(
      allLocks.filter(l => l.contextType === 'work').map(l => [l.contextId, l]),
    );
    return allTasks.map(task => {
      const lock = task.workId ? workLocks.get(task.workId) : undefined;
      return lock
        ? {
            ...task,
            lockedBy: lock.lockedBy,
          }
        : task;
    });
  }, [allTasks, allLocks, users]);

  const filteredTasks = useSearchedTasks(
    tasksWithLocks,
    selectedTaskType,
    searchText,
  );

  const handleSetSelectedTaskType = useCallback(
    (taskType: TaskTypeAll) => {
      dispatch(overviewSelectedTaskTypeUpdated(taskType));
    },
    [dispatch],
  );

  return {
    allTasks: tasksWithLocks,
    filteredTasks,
    taskTypes: getTaskTypes(selectedTaskType),
    selectedTaskType,
    setSelectedTaskType: handleSetSelectedTaskType,
    searchText,
    setSearchText,
    loading:
      status === QueryStatus.uninitialized || status === QueryStatus.pending,
    errorMsg: error ? 'Failed' : undefined,
  };
};
